import React from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import { StationProvisioning } from "../../../../hooks/useStationProvisioning";
import LadeverbundToggle from "./LadeverbundToggle";
import { provisioningErrorMessage } from "./provisioningErrors";
import ProvisioningStatusTag from "./ProvisioningStatusTag";

interface ReviewAction {
  label: string;
  icon: string;
  severity?: "danger" | "secondary";
  outlined?: boolean;
  text?: boolean;
}

// The button says what the review is for, so a station waiting on ops reads
// as a task rather than as one more status (SOPS-148 feedback).
export const reviewAction = (status: string): ReviewAction => {
  switch (status) {
    case "FAILED":
      return { label: "See failure & retry", icon: "pi pi-refresh", severity: "danger", outlined: true };
    case "NEEDS_MANUAL_ACTION":
      return { label: "See what to do", icon: "pi pi-exclamation-triangle", severity: "danger", outlined: true };
    case "COLLECTING":
      return { label: "See what's missing", icon: "pi pi-list", severity: "secondary", outlined: true };
    default:
      return { label: "Details", icon: "pi pi-eye", text: true };
  }
};

interface ProvisioningCellProps {
  stationId: string;
  entry?: StationProvisioning;
  onReview: () => void;
  // Re-reads the station; given, the Ladeverbund checkbox is offered here (SOPS-174).
  onChanged?: () => Promise<unknown> | void;
}

/** A station's eRound provisioning status in the stations table (SOPS-148). */
const ProvisioningCell: React.FC<ProvisioningCellProps> = ({ stationId, entry, onReview, onChanged }) => {
  if (!entry || entry.state === "loading") {
    return <span className="text-500">Loading…</span>;
  }
  if (entry.state === "unavailable") {
    return (
      <span title={provisioningErrorMessage(entry.error, "load")}>
        <Tag value="Unavailable" severity="warning" />
      </span>
    );
  }
  const { record } = entry;
  if (!record) {
    return <ProvisioningStatusTag status="NOT_TRACKED" />;
  }
  return (
    <div>
      <div className="flex flex-wrap align-items-center gap-2">
        <ProvisioningStatusTag status={record.status} waitingOn={record.waitingOn} />
        {record.alreadyInEround && <Tag value="Already in eRound" severity="info" />}
        <ReviewButton stationId={stationId} status={record.status} onReview={onReview} />
      </div>
      {onChanged && <LadeverbundToggle record={record} onChanged={onChanged} />}
    </div>
  );
};

export const ReviewButton: React.FC<{ stationId: string; status: string; onReview: () => void }> = ({
  stationId,
  status,
  onReview,
}) => {
  const action = reviewAction(status);
  return (
    <Button
      label={action.label}
      icon={action.icon}
      size="small"
      severity={action.severity}
      outlined={action.outlined}
      text={action.text}
      aria-label={`${action.label} – eRound provisioning for ${stationId}`}
      onClick={onReview}
    />
  );
};

export default ProvisioningCell;
